import classNames from 'classnames';
import { useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import styles from './count-up.module.scss';

export interface CountUpProps {
  /** The class name to apply */
  className?: string;
  /** Duration of the animation in milliseconds */
  duration?: number;
  /** Formatter function to format the displayed value */
  formatter?: (value: number) => string;
  /** The target value to count up to */
  value: number;
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 2.5);

const CountUp = ({
  className,
  duration = 1000,
  formatter,
  value,
}: CountUpProps) => {
  const [count, setCount] = useState(0);
  const [inView, setInView] = useState(false);
  const counterRef = useRef<HTMLDivElement | null>(null);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 },
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || shouldReduceMotion) {
      setCount(value);

      return;
    }

    let start: number | null = null;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;

      const progress = Math.min((timestamp - start) / duration, 1);
      const easedProgress = easeOut(progress);

      setCount(Math.floor(easedProgress * value));

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    const animationFrame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrame);
  }, [inView, value, duration, shouldReduceMotion]);

  return (
    <div ref={counterRef} className={classNames(styles.component, className)}>
      <div>{formatter ? formatter(count) : count}</div>
    </div>
  );
};

CountUp.displayName = 'CountUp';

export default CountUp;
